/**
 * Staff-vs-client detection for share-link analytics.
 *
 * When a user visits any internal page (`/share`, `/proposal-search`,
 * `/staff-rfqs`, etc.) we drop a `ms_staff=1` cookie. Cookie is
 * `SameSite=Lax` + `Max-Age=30d` rolling, so the same browser
 * landing on a `/p/{token}` public link a week later is correctly
 * identified as staff and their open does NOT count as a client view.
 *
 * The analytics tracker reads the cookie (here and server-side) and
 * tags the session as ``source: "staff-preview"`` so the dashboards
 * exclude it from the activity feed, the engagement score and the
 * per-token pill. The explicit ``?as=staff`` query param is still
 * supported as a manual override (useful when QA'ing from a fresh
 * incognito browser).
 *
 * Why a cookie vs. localStorage? Cookies travel on every request to
 * the same origin, so the backend can enforce ``staff-preview``
 * even if the client-side hook fails or is bypassed. localStorage
 * would only be visible to the JS hook, leaving a hole.
 */

const STAFF_COOKIE = "ms_staff";
const STAFF_COOKIE_VALUE = "1";
const ONE_MONTH_S = 60 * 60 * 24 * 30;

/** Drop the staff cookie on the current origin. Idempotent -
 *  re-set on every internal-page mount so the 30-day rolling
 *  expiry keeps refreshing while staff use the app. */
export function setStaffCookie() {
  if (typeof document === "undefined") return;
  try {
    document.cookie =
      `${STAFF_COOKIE}=${STAFF_COOKIE_VALUE}; ` +
      `path=/; max-age=${ONE_MONTH_S}; SameSite=Lax`;
  } catch {
    // Browsers with cookies disabled (private browsing on some
    // configs) - fall through; the backend enforcement still
    // holds via the explicit ?as=staff override.
  }
}

/** Read whether the current browser is recognised as a staff
 *  browser. Used by the analytics hook to pick the initial
 *  ``source`` for tracking events. */
export function isStaffBrowser() {
  if (typeof document === "undefined") return false;
  try {
    return document.cookie
      .split(";")
      .map((c) => c.trim())
      .some((c) => c === `${STAFF_COOKIE}=${STAFF_COOKIE_VALUE}`);
  } catch {
    return false;
  }
}

/** Manual override for QA - wipes the cookie so the next public
 *  link open is recorded as a client. Not wired to any UI yet; kept
 *  for forced-clear if we ever expose a "test as client" toggle. */
export function clearStaffCookie() {
  if (typeof document === "undefined") return;
  try {
    document.cookie = `${STAFF_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
  } catch {
    /* ignore */
  }
}
